import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCart, clearCart, CartItem } from '../../utils/cartUtils';
import { bookingService, BookingFormData } from '../../services/bookingService';
import { authService } from '../../services/authService';
import { paymentService } from '../../services/paymentService';
import { discountService, Discount } from '../../services/discountService';
import {
  trackBeginCheckout,
  trackAddPaymentInfo,
  type GA4Item,
} from '../../utils/analytics';
import './PaymentPage.css';

interface PaymentState {
  items: CartItem[];
  totalAmount: number;
}

// New group booking payment state
interface GroupBookingPaymentState {
  isGroupBooking: boolean;
  groupBookingId: string;
  serviceRequests: any[];
  totalAmount: number;
  services?: any[];
  serviceName?: string;
  selectedPlan?: any;
  selectedDate?: string;
  selectedTime?: string;
  selectedAddress?: any;
  isBookingPayment?: boolean;
  cityCode?: string;
  discountCode?: string;
  performAtAntaryatra?: boolean; // Whether service is performed at Antaryatra site
}

interface BookingPaymentState {
  bookingId: string;
  serviceName: string;
  totalAmount: number | string;
  selectedDate: string;
  selectedTime: string;
  selectedAddress: any;
  paymentMode: 'prepaid';
}

interface GSTBillingInfo {
  billingName: string;
  gstNumber: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  isDefault: boolean;
}

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [paymentMethod, setPaymentMethod] = useState('');

  // Helper function to calculate GST-inclusive amount
  const calculateGSTInclusiveAmount = (baseAmount: number | string): number => {
    let amount: number;
    
    if (typeof baseAmount === 'string') {
      // Remove any non-numeric characters except decimal point and convert to number
      const cleanString = baseAmount.replace(/[^0-9.-]+/g, '');
      amount = parseFloat(cleanString);
    } else {
      amount = baseAmount;
    }
    
    // Validate the amount
    if (isNaN(amount) || amount <= 0) {
      console.error('❌ Invalid amount for GST calculation:', { baseAmount, parsedAmount: amount });
      return 0; // Return 0 instead of NaN
    }
    
    const gstInclusiveAmount = amount + (amount * 0.18); // Add 18% GST
    const result = Math.round(gstInclusiveAmount * 100) / 100; // Round to 2 decimal places
    
    console.log('💰 GST Calculation:', { 
      baseAmount, 
      parsedAmount: amount, 
      gstInclusiveAmount, 
      finalResult: result 
    });
    
    return result;
  };

  // Helper function to get plan code from plan name
  const getPlanCodeFromName = (planName: string): string => {
    const planCodeMapping: { [key: string]: string } = {
      'basic': 'BASIC',
      'standard': 'STANDARD', 
      'premium': 'PREMIUM',
      'deluxe': 'DELUXE',
      'ultimate': 'ULTIMATE'
    };
    
    const normalizedPlanName = planName.toLowerCase().trim();
    return planCodeMapping[normalizedPlanName] || 'STANDARD';
  };

  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<Discount | null>(null);
  const [availableDiscounts, setAvailableDiscounts] = useState<Discount[]>([]);
  const [isLoadingDiscounts, setIsLoadingDiscounts] = useState(false);
  const [discountError, setDiscountError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBookingPayment, setIsBookingPayment] = useState(false);
  const [bookingData, setBookingData] = useState<BookingPaymentState | null>(null);
  const [groupBookingData, setGroupBookingData] = useState<GroupBookingPaymentState | null>(null);
  const [isGroupBooking, setIsGroupBooking] = useState(false);
  const [isGSTRequired, setIsGSTRequired] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastBookingId, setLastBookingId] = useState<string | null>(null);
  const [isFromOrdersPage, setIsFromOrdersPage] = useState(false);

  // ── Inline validation errors (replaces alert()) ─────────────────────────
  const [inlineError, setInlineError] = useState<string>('');
  const paymentMethodSectionRef = useRef<HTMLDivElement>(null);
  const inlineErrorRef = useRef<HTMLDivElement>(null);

  const showInlineError = useCallback((message: string, scrollTo?: 'methods' | 'error') => {
    setInlineError(message);
    setTimeout(() => {
      const target = scrollTo === 'methods'
        ? paymentMethodSectionRef.current
        : inlineErrorRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, []);

  // GST Billing form state
  const [gstBillingInfo, setGSTBillingInfo] = useState<GSTBillingInfo>({
    billingName: '',
    gstNumber: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    isDefault: false
  });

  // Fetch available discounts on component mount
  useEffect(() => {
    const fetchAvailableDiscounts = async () => {
      try {
        setIsLoadingDiscounts(true);
        console.log('💳 Starting to fetch available discounts...');
        const discounts = await discountService.getAvailableDiscounts();
        setAvailableDiscounts(discounts);
        console.log('💳 Available discount codes loaded:', discounts.map(d => `${d.code} (${d.percentage}%)`));
        console.log('💳 Discount section conditions:', {
          isFromOrdersPage,
          isRetryPayment: location.state?.isRetryPayment,
          isPostpaidPayment: location.state?.isPostpaidPayment,
          orderId: location.state?.orderId,
          shouldShowDiscounts: (!isFromOrdersPage || (!location.state?.isRetryPayment && !location.state?.isPostpaidPayment))
        });
      } catch (error) {
        console.error('❌ Error fetching available discounts:', error);
      } finally {
        setIsLoadingDiscounts(false);
      }
    };

    fetchAvailableDiscounts();
  }, [isFromOrdersPage, location.state]);

  // Billing form state (kept for compatibility)
  const [billingInfo, setBillingInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    // Check payment type and set appropriate state
    const state = location.state as any; // Use any temporarily to handle different state types
    
    console.log('💳 PaymentPage received state:', state);
    
    if (state?.groupBookingId) {
      // New group booking structure
      console.log('💳 Group booking payment detected:', state);
      setIsGroupBooking(true);
      setGroupBookingData(state as GroupBookingPaymentState);
      setIsBookingPayment(true);
      // Detect orders page payment: totalAmount is already GST-inclusive, must not add GST again
      if (state.fromOrdersPage || state.isRetryPayment || state.isPostpaidPayment || state.orderId) {
        console.log('🔄 Orders page group payment detected - GST already included in totalAmount');
        setIsFromOrdersPage(true);
      }
    } else if (state?.bookingId) {
      // Legacy / orders-page single booking payment
      setIsBookingPayment(true);
      setBookingData(state as BookingPaymentState);
      // Detect orders page payment: totalAmount is already GST-inclusive, must not add GST again
      if (state.fromOrdersPage || state.isRetryPayment || state.isPostpaidPayment || state.orderId) {
        console.log('🔄 Orders page single payment detected - GST already included in totalAmount');
        setIsFromOrdersPage(true);
      }
    } else if (state?.serviceName && state?.isBookingPayment) {
      // Direct service booking from BookingPage OR payment from Orders
      console.log('💳 Service booking payment detected:', {
        isRetry: state.isRetryPayment,
        isPostpaid: state.isPostpaidPayment,
        orderId: state.orderId,
        isGroupBooking: state.isGroupBooking,
        groupBookingId: state.groupBookingId
      });
      setIsBookingPayment(true);
      
      // Handle payments from Orders page - detect by orderId presence
      if (state.orderId || state.isRetryPayment || state.isPostpaidPayment || state.fromOrdersPage) {
        console.log('🔄 Orders page payment detected - setting up payment flow');
        setIsFromOrdersPage(true);
        console.log('🔄 isFromOrdersPage set to true');
        
        // If this is a group booking from orders, keep group payment together
        if (state.isGroupBooking && state.groupBookingId) {
          console.log('🔄 Setting up group payment from Orders - payment together approach');
          setIsGroupBooking(true);
          // Create complete group booking data for payment together
          setGroupBookingData({
            isGroupBooking: true,
            groupBookingId: state.groupBookingId,
            totalAmount: state.totalAmount || 0, // This will be calculated by backend for all services in group
            serviceRequests: [{
              serviceId: 1,
              serviceName: `Group Booking Payment (${state.serviceName})`, // Show which service initiated payment
              selectedPlan: null,
              selectedDate: state.selectedDate,
              selectedTime: state.selectedTime,
              selectedAddress: state.selectedAddress,
              isConfigured: true
            }]
          });
        } else {
          console.log('🔄 Setting up individual service payment from Orders');
          setIsGroupBooking(false);
          setGroupBookingData(null);
        }
      }
      // Note: We'll handle this case in the payment processing logic
    } else {
      // Cart payment
      const items = state?.items || getCart();
      if (items.length === 0) {
        navigate('/cart');
        return;
      }
      setCartItems(items);
    }
  }, [location.state, navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ── GA4: begin_checkout — fire once when payment page loads with order data ──
  useEffect(() => {
    const state = location.state as any;
    if (!state) return;

    const buildGA4Items = (): GA4Item[] => {
      if (state.serviceRequests && state.serviceRequests.length > 0) {
        return state.serviceRequests.map((sr: any, idx: number) => ({
          item_id: String(sr.serviceId || `svc_${idx}`),
          item_name: sr.serviceName || 'Puja Service',
          item_category: 'Puja Service',
          item_variant: sr.selectedPlan?.name || sr.planName,
          price: typeof sr.priceEstimate === 'string'
            ? parseFloat(sr.priceEstimate)
            : sr.priceEstimate ?? 0,
          quantity: 1,
          currency: 'INR' as const,
        }));
      }
      if (state.serviceName) {
        return [{
          item_id: state.serviceId || state.bookingId || state.groupBookingId || 'service',
          item_name: state.serviceName,
          item_category: 'Puja Service',
          item_variant: state.planName,
          price: typeof state.totalAmount === 'string'
            ? parseFloat(state.totalAmount.replace(/[^0-9.-]+/g, ''))
            : state.totalAmount ?? 0,
          quantity: 1,
          currency: 'INR' as const,
        }];
      }
      return cartItems.map(item => ({
        item_id: item.id || item.name,
        item_name: item.name,
        item_category: 'Puja Service',
        price: typeof item.price === 'string'
          ? parseFloat(item.price.replace(/[^0-9.-]+/g, ''))
          : item.price ?? 0,
        quantity: item.quantity ?? 1,
        currency: 'INR' as const,
      }));
    };

    const totalValue =
      typeof state.totalAmount === 'string'
        ? parseFloat(state.totalAmount.replace(/[^0-9.-]+/g, ''))
        : state.totalAmount ?? 0;

    trackBeginCheckout(buildGA4Items(), totalValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fire once on mount

  // Calculate pricing
const getSubtotal = () => {
    if (groupBookingData) {
      // For orders-page payments: totalAmount is already GST-inclusive (isFromOrdersPage=true → getTaxAmount returns 0)
      // For new BookingPage payments: totalAmount is the base amount (getTaxAmount adds 18% GST on top)
      return groupBookingData.totalAmount;
    } else if (location.state?.serviceName && location.state?.totalAmount) {
      // Direct service booking from BookingPage OR Orders page payment
      
      // If coming from Orders page (retry/postpaid), amount already includes GST
      if (location.state?.isRetryPayment || location.state?.isPostpaidPayment || isFromOrdersPage) {
        const amount = location.state.totalAmount;
        const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount;
        console.log('💰 Orders page payment - using full GST-inclusive amount:', numericAmount);
        return numericAmount; // Use full amount since getTaxAmount() will return 0
      }
      
      // For cart-originated bookings, use baseAmount if available (original cart item price)
      if (location.state?.baseAmount) {
        const baseAmount = typeof location.state.baseAmount === 'string' 
          ? parseFloat(location.state.baseAmount.replace(/[^0-9.-]+/g, "")) 
          : location.state.baseAmount;
        return baseAmount; // Use original cart item base price
      }
      
      // BookingPage always sends GST-inclusive totalAmount — use it directly
      const amount = location.state.totalAmount;
      const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount;
      return numericAmount; // Already GST-inclusive, do not divide by 1.18
    } else {
      // Cart payment - these should be base prices
      return cartItems.reduce((total, item) => {
        const price = parseFloat(item.price.replace(/[^0-9.-]+/g, ""));
        return total + (price * item.quantity);
      }, 0);
    }
  };

  const getDiscountAmount = () => {
    // Don't apply additional discounts for orders page payments
    if (location.state?.isRetryPayment || location.state?.isPostpaidPayment || isFromOrdersPage) {
      return 0; // No additional discount - any discount was already applied in original booking
    }
    const subtotal = getSubtotal();
    
    if (!appliedDiscount) return 0;
    return discountService.calculateDiscountAmount(appliedDiscount, subtotal);
  };

  const getTaxAmount = () => {
    // GST is always already included in DB prices (totalAmount is GST-inclusive).
    // This applies to all BookingPage bookings, group bookings, and Orders page payments.
    // For pure cart payments with static (non-DB) fallback prices, apply 18% GST.
    if (location.state?.isRetryPayment || location.state?.isPostpaidPayment || isFromOrdersPage) {
      return 0; // GST already included in the order amount
    }
    if (groupBookingData || location.state?.serviceName) {
      return 0; // GST already included in BookingPage/group booking amounts
    }
    // Pure cart payment with static prices — apply 18% GST
    const subtotal = getSubtotal();
    const discountAmount = getDiscountAmount();
    return ((subtotal - discountAmount) * 18) / 100;
  };

  const getTotalAmount = () => {
    const subtotal = getSubtotal();
    const discountAmount = getDiscountAmount();
    const taxAmount = getTaxAmount();
    return subtotal - discountAmount + taxAmount;
  };

  const getItemCount = () => {
    if (groupBookingData) {
      // Count group services
      return groupBookingData.serviceRequests?.length || 1;
    } else if (location.state?.serviceName) {
      return 1; // Single service booking (both legacy and direct)
    } else {
      return cartItems.reduce((total, item) => total + item.quantity, 0);
    }
  };

  const handleDiscountApply = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    setIsLoadingDiscounts(true);
    setDiscountError('');

    try {
      // Get current cart items for validation context
      const currentCartItems = isGroupBooking ? 
        (groupBookingData?.services || []).map(service => ({
          id: service.cartItem?.id || 'unknown',
          name: service.cartItem?.name || 'Unknown Service',
          price: service.selectedPlan?.price || '₹0',
          quantity: 1
        })) :
        (bookingData ? [
          {
            id: bookingData.bookingId || 'single',
            name: bookingData.serviceName || 'Single Service',
            price: bookingData.totalAmount?.toString() || '₹0',
            quantity: 1
          }
        ] : getCart());

      const validation = await discountService.validateDiscountCode(discountCode, currentCartItems);
      
      if (validation.valid && validation.discount) {
        setAppliedDiscount(validation.discount);
        setDiscountError('');
        console.log('✅ Discount applied:', validation.discount.code, '-', validation.discount.percentage + '%');
        
        // Show success message if provided
        if (validation.message) {
          // You could show a toast notification here
          console.log('ℹ️ Discount message:', validation.message);
        }
      } else {
        setAppliedDiscount(null);
        setDiscountError(validation.message || 'Invalid discount code');
        console.log('❌ Discount validation failed:', validation.message);
      }
    } catch (error) {
      console.error('❌ Error applying discount:', error);
      setAppliedDiscount(null);
      setDiscountError('Unable to validate discount code. Please try again.');
    } finally {
      setIsLoadingDiscounts(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBillingInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGSTInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let filteredValue = value;

    // Apply specific validations based on field name
    if (name === 'pincode') {
      // Only allow digits and limit to 6 characters for pincode
      filteredValue = value.replace(/[^0-9]/g, '').slice(0, 6);
    } else if (name === 'billingAddressLine1') {
      // Limit address line 1 to 100 characters
      filteredValue = value.slice(0, 100);
    } else if (name === 'billingAddressLine2') {
      // Limit address line 2 to 100 characters
      filteredValue = value.slice(0, 100);
    }

    setGSTBillingInfo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : filteredValue
    }));
  };

  const validateGSTNumber = (gstNumber: string): boolean => {
    // Basic GST number validation (15 characters, alphanumeric)
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber.toUpperCase());
  };

  // ── GA4: add_payment_info — fire when user selects a payment method ──────────
  const handlePaymentMethodChange = (method: string) => {
    setPaymentMethod(method);
    const state = location.state as any;
    const totalValue =
      typeof state?.totalAmount === 'string'
        ? parseFloat(state.totalAmount.replace(/[^0-9.-]+/g, ''))
        : state?.totalAmount ?? 0;
    const label = method === 'paylater' ? 'Pay Later' : method === 'paynow' ? 'Razorpay' : method;
    trackAddPaymentInfo([], totalValue, label);
  };

  const handlePayment = async () => {
    if (!paymentMethod) {
      showInlineError('Please select a payment method below.', 'methods');
      // Also shake the payment method section for emphasis
      paymentMethodSectionRef.current?.classList.add('shake-error');
      setTimeout(() => paymentMethodSectionRef.current?.classList.remove('shake-error'), 600);
      return;
    }

    // Clear previous errors
    setPaymentError(null);
    setInlineError('');

    // Validate GST information if required
    if (isGSTRequired) {
      if (!gstBillingInfo.billingName || !gstBillingInfo.billingAddressLine1 || 
          !gstBillingInfo.city || !gstBillingInfo.state || !gstBillingInfo.pincode ||
          !gstBillingInfo.phone || !gstBillingInfo.email) {
        showInlineError('Please fill in all required GST billing information.');
        return;
      }
      
      if (gstBillingInfo.gstNumber && !validateGSTNumber(gstBillingInfo.gstNumber)) {
        showInlineError('Please enter a valid GST number (15 characters, format: 22AAAAA0000A1Z5).');
        return;
      }
    }

    // Check authentication for service bookings
    if (!authService.isAuthenticated() && location.state?.serviceName) {
      showInlineError('Please log in to complete your booking.');
      navigate('/login');
      return;
    }

    setIsProcessing(true);

    try {
      if (isBookingPayment && bookingData) {
        // Handle existing booking payment (for bookings created earlier)
        console.log('Processing booking payment for booking:', bookingData.bookingId);
        
        try {
          // Get customer info
          const userProfile = await authService.getCurrentUser().catch(() => null);
          const customerInfo = {
            name: billingInfo.name || userProfile?.firstName + ' ' + userProfile?.lastName || 'Customer',
            email: billingInfo.email || userProfile?.email || '',
            phone: billingInfo.phone || ''
          };

          // Complete payment flow with Razorpay
          const totalAmountValue = typeof bookingData.totalAmount === 'string' 
            ? parseFloat(bookingData.totalAmount.replace(/[^0-9.-]+/g, "")) 
            : bookingData.totalAmount;
            
          const verificationResult = await paymentService.completePaymentFlow(
            bookingData.bookingId,
            customerInfo,
            {
              description: `Payment for ${bookingData.serviceName}`,
              totalAmount: totalAmountValue
            }
          );

          if (verificationResult.success) {
            console.log('✅ Booking payment verified successfully');
            
            navigate('/payment-success', {
              state: {
                bookingId: bookingData.bookingId,
                orderId: `SRQ-${Date.now().toString().slice(-6)}`,
                // totalAmount from orders page already includes GST — do not add GST again
                amount: totalAmountValue,
                serviceName: bookingData.serviceName,
                selectedDate: bookingData.selectedDate,
                selectedTime: bookingData.selectedTime,
                selectedAddress: bookingData.selectedAddress,
                billingInfo,
                paymentMethod,
                paymentStatus: 'completed',
                isBookingPayment: true
              }
            });
          } else {
            throw new Error(verificationResult.message || 'Payment verification failed');
          }
        } catch (paymentError) {
          console.error('❌ Booking payment failed:', paymentError);
          
          let errorMessage = 'Payment failed. Please try again.';
          if (paymentError instanceof Error && paymentError.message.includes('cancelled')) {
            errorMessage = 'Payment was cancelled. You can try again anytime.';
          }
          
          setPaymentError(errorMessage);
          return;
        }
        
      } else if (isGroupBooking && groupBookingData) {
        // Handle new group booking payment
        console.log('Processing group booking payment:', groupBookingData);
        
        try {
          console.log('💳 Starting group booking payment flow...');
          
          // First, check if we need to create the group booking
          let actualGroupBookingId = groupBookingData.groupBookingId;
          
          if (groupBookingData.groupBookingId.startsWith('temp_')) {
            // Create the actual group booking first
            const serviceRequests: any[] = (groupBookingData.serviceRequests || []).map((serviceRequest: any) => {
              const slotStart = new Date(`${serviceRequest.selectedDate}T${serviceRequest.selectedTime}`);
              const formattedSlotStart = slotStart.toISOString(); // Send in UTC format to prevent double conversion
              
              // Apply discount to service price estimate if any
              const originalPrice = typeof serviceRequest.priceEstimate === 'string' 
                ? parseFloat(serviceRequest.priceEstimate) 
                : serviceRequest.priceEstimate || 0;
              const discountAmount = appliedDiscount 
                ? discountService.calculateDiscountAmount(appliedDiscount, originalPrice)
                : 0;
              const discountedPrice = originalPrice - discountAmount;
              
              // Extract maxProviders from selectedPlan
              const maxProviders = serviceRequest.selectedPlan?.priests || 1;
              
              return {
                customerId: authService.getUserUuid(),
                serviceId: serviceRequest.serviceId,
                slotStart: formattedSlotStart,
                slotEnd: '', // Will be calculated by backend
                requestStatus: 'PENDING_PROVIDER' as const,
                paymentMode: paymentMethod === 'paylater' ? 'POSTPAID' : 'PREPAID',
                paymentStatus: paymentMethod === 'paylater' ? 'NOT_REQUIRED' : 'PENDING',
                priceEstimate: discountedPrice.toString(), // Send discounted price
                assignedProviderId: '',
                addressId: serviceRequest.selectedAddress 
                  ? (serviceRequest.selectedAddress.addressUuid || serviceRequest.selectedAddress.id)
                  : null, // Send null when performing at Antaryatra site
                maxProviders: maxProviders,
                planCode: serviceRequest.selectedPlan?.name ? getPlanCodeFromName(serviceRequest.selectedPlan.name) : serviceRequest.planName ? getPlanCodeFromName(serviceRequest.planName) : 'STANDARD',
                cityCode: groupBookingData.cityCode || serviceRequest.cityCode || 'VARANASI', // Default to VARANASI as requested
                discountCode: appliedDiscount ? appliedDiscount.code : undefined, // Use applied discount code
                serviceLocationType: groupBookingData.performAtAntaryatra ? 'PLATFORM_SITE' : 'USER_SITE' // Add service location type
              };
            });
            
            console.log('🚀 Creating group booking first:', serviceRequests);
            const groupBookingResponse = await bookingService.createGroupBooking(serviceRequests);
            console.log('✅ Group booking created:', groupBookingResponse);
            
            actualGroupBookingId = groupBookingResponse.groupBookingId;
          }
          
          // Create payment order using the unified payment API
          const userProfile = await authService.getCurrentUser().catch(() => null);
          
          // Calculate discounted total amount for group booking payment
          const originalTotalAmount = groupBookingData.totalAmount;
          const discountAmountTotal = appliedDiscount 
            ? discountService.calculateDiscountAmount(appliedDiscount, originalTotalAmount)
            : 0;
          const discountedTotalAmount = originalTotalAmount - discountAmountTotal;
          
          const paymentOrderRequest = {
            groupBookingId: actualGroupBookingId,
            customerName: billingInfo.name || userProfile?.firstName + ' ' + userProfile?.lastName || 'Customer',
            customerEmail: billingInfo.email || userProfile?.email || '',
            customerPhone: billingInfo.phone || '',
            totalAmount: discountedTotalAmount, // Use discounted amount for payment
            paymentMode: (paymentMethod === 'paylater' ? 'POSTPAID' : 'PREPAID') as 'PREPAID' | 'POSTPAID',
            gstBillingInfo: isGSTRequired ? gstBillingInfo : undefined
          };

          console.log('🚀 Creating payment order for group booking:', {
            ...paymentOrderRequest,
            originalAmount: originalTotalAmount,
            discountPercent: appliedDiscount,
            discountAmount: discountAmountTotal,
            finalTotalAmount: discountedTotalAmount
          });
          const paymentOrderResponse = await paymentService.createPaymentOrder(paymentOrderRequest);
          console.log('✅ Payment order created successfully:', paymentOrderResponse);

          if (paymentMethod === 'paylater') {
            // Pay later flow - order is created but payment is pending
            navigate('/payment-success', {
              state: {
                isGroupBooking: true,
                groupBookingId: actualGroupBookingId,
                orderId: actualGroupBookingId, // Use group booking ID as the order ID
                amount: getTotalAmount(), // Show GST-inclusive amount that user sees on payment button
                services: groupBookingData.serviceRequests || [],
                billingInfo,
                gstBillingInfo: isGSTRequired ? gstBillingInfo : undefined,
                paymentMethod: 'paylater',
                paymentMode: 'POSTPAID',
                paymentStatus: 'pending',
                paymentDueDate: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
                isBookingPayment: true
              }
            });
          } else {
            // Pay now flow - process payment with Razorpay using the payment order response
            const userProfile = await authService.getCurrentUser().catch(() => null);
            const customerInfo = {
              name: billingInfo.name || userProfile?.firstName + ' ' + userProfile?.lastName || 'Customer',
              email: billingInfo.email || userProfile?.email || '',
              phone: billingInfo.phone || ''
            };

            // Process payment directly with the payment order
            const razorpayResponse = await paymentService.processPayment(
              paymentOrderResponse,
              customerInfo,
              {
                description: `Payment for group booking (${groupBookingData.serviceRequests?.length || 1} services)`,
              }
            );

            // Verify payment
            const verificationResult = await paymentService.verifyPayment({
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });

            if (verificationResult.success) {
              console.log('✅ Group booking payment verified successfully');
              
              navigate('/payment-success', {
                state: {
                  isGroupBooking: true,
                  groupBookingId: actualGroupBookingId,
                  orderId: actualGroupBookingId, // Use group booking ID as the order ID
                  amount: getTotalAmount(), // Show GST-inclusive amount that user sees on payment button
                  services: groupBookingData.serviceRequests || [],
                  billingInfo,
                  gstBillingInfo: isGSTRequired ? gstBillingInfo : undefined,
                  paymentMethod,
                  paymentMode: 'PREPAID',
                  paymentStatus: 'completed',
                  isBookingPayment: true
                }
              });
            } else {
              throw new Error(verificationResult.message || 'Payment verification failed');
            }
          }
        } catch (paymentError) {
          console.error('❌ Group booking payment failed:', paymentError);
          
          let errorMessage = 'Group booking payment failed. Please try again.';
          if (paymentError instanceof Error && paymentError.message.includes('cancelled')) {
            errorMessage = 'Payment was cancelled. You can try again anytime.';
          }
          
          setPaymentError(errorMessage);
          return;
        }

      } else if (location.state?.serviceName) {
        // Handle new service booking from BookingPage
        console.log('Creating new service booking...');
        
        // Calculate the discounted price estimate
        const originalAmount = typeof location.state.totalAmount === 'string' 
          ? parseFloat(location.state.totalAmount.replace(/[^0-9.-]+/g, "")) 
          : location.state.totalAmount;
        const discountAmount = appliedDiscount 
          ? discountService.calculateDiscountAmount(appliedDiscount, originalAmount)
          : 0;
        const discountedAmount = originalAmount - discountAmount;
        
        const bookingData: BookingFormData = {
          serviceId: location.state.serviceId,
          serviceName: location.state.serviceName,
          selectedDate: location.state.selectedDate,
          selectedTime: location.state.selectedTime,
          duration: typeof location.state.duration === 'string' ? parseInt(location.state.duration) : location.state.duration || 60,
          priceEstimate: discountedAmount, // Send discounted amount so API calculates GST on discounted price
          addressId: location.state.selectedAddress 
            ? (location.state.selectedAddress.addressUuid || location.state.selectedAddress.id)
            : null, // Send null when performing at Antaryatra site
          paymentMode: paymentMethod === 'paylater' ? 'POSTPAID' : 'PREPAID',
          selectedPlan: location.state.selectedPlan, // Include selectedPlan for maxProviders extraction
          planCode: location.state.planCode || 'STANDARD',
          cityCode: location.state.cityCode || 'DEL',
          discountCode: appliedDiscount ? appliedDiscount.code : undefined,
          performAtAntaryatra: location.state.performAtAntaryatra || false // Add service location information
        };

        console.log('🚀 Creating booking with data:', {
          ...bookingData,
          originalAmount,
          discountPercent: appliedDiscount,
          discountAmount,
          finalPriceEstimate: discountedAmount
        });

        const booking = await bookingService.createServiceBooking(bookingData);

        console.log('✅ Booking created successfully:', booking);

        // Get the first (and only) service request from the response
        const serviceRequest = booking.serviceRequests[0];

        if (paymentMethod === 'paylater') {
          // Pay later flow - booking is confirmed but payment is pending
          navigate('/payment-success', {
            state: {
              bookingId: serviceRequest.serviceRequestUuid, // Use UUID from API response
              orderId: `SRQ-${serviceRequest.id.toString().padStart(6, '0')}`, // Use proper format from API
              amount: getTotalAmount(), // Show GST-inclusive amount that user sees on payment button
              serviceName: location.state.serviceName,
              selectedDate: serviceRequest.slotStart, // Use API response with timezone
              selectedTime: serviceRequest.slotStart, // Use API response with timezone
              selectedAddress: location.state.selectedAddress,
              billingInfo,
              paymentMethod: 'paylater',
              paymentMode: 'POSTPAID',
              paymentStatus: 'pending',
              paymentDueDate: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
              isBookingPayment: true,
              duration: location.state.duration, // Keep original duration string
              planName: location.state.planName,
              serviceImage: location.state.serviceImage
            }
          });
        } else {
          // Pay now flow - process payment with Razorpay
          try {
            console.log('💳 Starting Razorpay payment flow...');
            
            // Get customer info from billing info or auth service
            const userProfile = await authService.getCurrentUser().catch(() => null);
            const customerInfo = {
              name: billingInfo.name || userProfile?.firstName + ' ' + userProfile?.lastName || 'Customer',
              email: billingInfo.email || userProfile?.email || '',
              phone: billingInfo.phone || ''
            };

            // Complete payment flow with Razorpay
            const verificationResult = await paymentService.completePaymentFlow(
              serviceRequest.serviceRequestUuid,
              customerInfo,
              {
                description: `Payment for ${location.state.serviceName}`,
                image: location.state.serviceImage,
                totalAmount: getTotalAmount() // Pass total amount including GST
              }
            );

            if (verificationResult.success) {
              console.log('✅ Payment verified successfully');
              
              navigate('/payment-success', {
                state: {
                  bookingId: serviceRequest.serviceRequestUuid,
                  orderId: `SRQ-${serviceRequest.id.toString().padStart(6, '0')}`,
                  amount: getTotalAmount(), // Show GST-inclusive amount that user sees on payment button
                  serviceName: location.state.serviceName,
                  selectedDate: serviceRequest.slotStart,
                  selectedTime: serviceRequest.slotStart,
                  selectedAddress: location.state.selectedAddress,
                  billingInfo,
                  paymentMethod,
                  paymentMode: 'PREPAID',
                  paymentStatus: 'completed',
                  isBookingPayment: true,
                  duration: location.state.duration,
                  planName: location.state.planName,
                  serviceImage: location.state.serviceImage
                }
              });
            } else {
              throw new Error(verificationResult.message || 'Payment verification failed');
            }
          } catch (paymentError) {
            console.error('❌ Razorpay payment error:', paymentError);
            
            // Don't immediately set error for payment failures - user might retry in modal
            if (paymentError instanceof Error && paymentError.message.includes('cancelled')) {
              // Only show error for actual cancellation by user
              setPaymentError('Payment was cancelled. You can try again anytime.');
              setLastBookingId(serviceRequest.serviceRequestUuid);
            } else {
              // For other errors, check if it's a retry case or genuine failure
              const errorMessage = paymentError instanceof Error && 
                paymentError.message.includes('failed') ?
                'Payment failed. Please check your details and try again.' :
                'An error occurred during payment. Please try again.';
              
              setPaymentError(errorMessage);
              setLastBookingId(serviceRequest.serviceRequestUuid);
            }
            return;
          }
        }

      } else {
        // Handle cart payment - existing logic
        if (paymentMethod === 'paylater') {
          // Simulate booking confirmation for pay later
          setTimeout(() => {
            clearCart();
            navigate('/payment-success', {
              state: {
                orderId: `ORD${Date.now()}`,
                amount: getTotalAmount(),
                items: cartItems,
                billingInfo,
                paymentMethod: 'paylater',
                paymentStatus: 'pending',
                paymentDueDate: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
                isBookingPayment: true, // Add this to show View Orders button
                serviceName: 'Cart Services' // Generic service name for cart
              }
            });
          }, 2000);
        } else {
          // Simulate regular payment processing
          setTimeout(() => {
            clearCart();
            navigate('/payment-success', {
              state: {
                orderId: `ORD${Date.now()}`,
                amount: getTotalAmount(),
                items: cartItems,
                billingInfo,
                paymentMethod,
                paymentStatus: 'completed',
                isBookingPayment: true, // Add this to show View Orders button
                serviceName: 'Cart Services' // Generic service name for cart
              }
            });
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Payment processing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Payment failed. Please try again.';
      setPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment retry
  const handleRetryPayment = async () => {
    if (!lastBookingId) return;
    
    setRetryCount(prev => prev + 1);
    setPaymentError(null);
    
    try {
      const userProfile = await authService.getCurrentUser().catch(() => null);
      const customerInfo = {
        name: billingInfo.name || userProfile?.firstName + ' ' + userProfile?.lastName || 'Customer',
        email: billingInfo.email || userProfile?.email || '',
        phone: billingInfo.phone || ''
      };

      const verificationResult = await paymentService.completePaymentFlow(
        lastBookingId,
        customerInfo,
        {
          description: `Payment for ${location.state.serviceName}`,
          image: location.state.serviceImage,
          totalAmount: getTotalAmount() // Pass total amount including GST
        }
      );

      if (verificationResult.success) {
        console.log('✅ Retry payment successful');
        
        navigate('/payment-success', {
          state: {
            bookingId: lastBookingId,
            orderId: `SRQ-${Date.now().toString().slice(-6)}`,
            amount: getTotalAmount(), // Show GST-inclusive amount that user sees on payment button
            serviceName: location.state.serviceName,
            selectedDate: location.state.selectedDate,
            selectedTime: location.state.selectedTime,
            selectedAddress: location.state.selectedAddress,
            billingInfo,
            paymentMethod,
            paymentMode: 'PREPAID',
            paymentStatus: 'completed',
            isBookingPayment: true,
            duration: location.state.duration,
            planName: location.state.planName,
            serviceImage: location.state.serviceImage
          }
        });
      }
    } catch (error) {
      console.error('Retry payment failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Retry failed. Please try again.';
      setPaymentError(errorMessage);
    }
  };

  if (!isBookingPayment && !location.state?.serviceName && cartItems.length === 0) {
    return <div>Loading...</div>;
  }

  // Only show loading for legacy booking payments that need bookingData
  if (isBookingPayment && !bookingData && !location.state?.serviceName && !groupBookingData) {
    return <div>Loading booking details...</div>;
  }

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <button 
            onClick={() => {
              if (isFromOrdersPage || location.state?.orderId || location.state?.fromOrdersPage) {
                // Navigate back to My Orders page
                navigate('/orders');
              } else if (location.state?.serviceName) {
                // Navigate back to the specific service booking page
                navigate(-1); // Go back to previous page (BookingPage)
              } else {
                navigate('/cart');
              }
            }} 
            className="back-btn"
          >
            ← Back to {
              (isFromOrdersPage || location.state?.orderId || location.state?.fromOrdersPage) ? 'My Orders' : 
              location.state?.serviceName ? 'Booking' : 'Cart'
            }
          </button>
          <h1>
            {location.state?.isRetryPayment ? 'Retry Payment' :
             location.state?.isPostpaidPayment ? 'Complete Payment' :
             location.state?.serviceName ? 'Complete Service Booking' : 'Checkout'}
          </h1>
          {(location.state?.isRetryPayment || location.state?.isPostpaidPayment) && (
            <div className="payment-context-banner">
              <span className="context-icon">
                {location.state?.isRetryPayment ? '🔄' : '⏰'}
              </span>
              <div className="context-info">
                <div className="context-title">
                  {location.state?.isRetryPayment ? 'Previous Payment Failed' : 'Pay for Your Service'}
                </div>
                <div className="context-subtitle">
                  {location.state?.isRetryPayment ? 
                    'Please retry your payment to confirm your booking' :
                    'Complete payment to confirm your service booking'}
                </div>
                {location.state?.orderId && (
                  <div className="context-order-id">Order ID: {location.state.orderId}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="payment-content">
          {/* Left Side - Payment */}
          <div className="payment-form-section">
            {/* Payment Method - FIRST */}
            <div ref={paymentMethodSectionRef} className="payment-method-section">
              <h2>Payment Method</h2>
              <div className="payment-options">
                <label className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <div className="payment-icon">💳</div>
                    <div>
                      <h3>Credit/Debit Card</h3>
                      <p>Pay securely with your card</p>
                    </div>
                  </div>
                </label>

                <label className={`payment-option ${paymentMethod === 'upi' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="upi"
                    checked={paymentMethod === 'upi'}
                    onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <div className="payment-icon">📱</div>
                    <div>
                      <h3>UPI Payment</h3>
                      <p>Pay using UPI apps like GPay, PhonePe</p>
                    </div>
                  </div>
                </label>

                <label className={`payment-option ${paymentMethod === 'netbanking' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="netbanking"
                    checked={paymentMethod === 'netbanking'}
                    onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <div className="payment-icon">🏦</div>
                    <div>
                      <h3>Net Banking</h3>
                      <p>Pay directly from your bank account</p>
                    </div>
                  </div>
                </label>

                <label className={`payment-option ${paymentMethod === 'wallet' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="wallet"
                    checked={paymentMethod === 'wallet'}
                    onChange={(e) => handlePaymentMethodChange(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <div className="payment-icon">👛</div>
                    <div>
                      <h3>Digital Wallet</h3>
                      <p>Paytm, Amazon Pay, JioMoney</p>
                    </div>
                  </div>
                </label>

                {/* Hide Pay Later option for orders page payments */}
                {!isFromOrdersPage && !location.state?.orderId && (
                  <label className={`payment-option pay-later ${paymentMethod === 'paylater' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment"
                      value="paylater"
                      checked={paymentMethod === 'paylater'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                    />
                    <div className="payment-option-content">
                      <div className="payment-icon">⏰</div>
                      <div>
                        <h3>Pay Later</h3>
                        <p>Book now with zero payment, pay in next 72 hours</p>
                        <div className="pay-later-features">
                          <span className="feature">• No advance payment required</span>
                          {/* <span className="feature">• Pay up to 72 hours before service</span> */}
                          <span className="feature">• Free cancellation until payment(as per cancellation policy)</span>
                        </div>
                      </div>
                    </div>
                    <div className="pay-later-badge">
                      <span>Popular</span>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* GST Information Section - SECOND - Hide for orders page payments */}
            {!isFromOrdersPage && 
             !location.state?.isRetryPayment && 
             !location.state?.isPostpaidPayment && 
             !location.state?.orderId && (
            <div className="gst-section">
              {/* <div className="gst-checkbox-container">
                <label className="gst-checkbox">
                  <input
                    type="checkbox"
                    checked={isGSTRequired}
                    onChange={(e) => setIsGSTRequired(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">GST Invoice Required</span>
                  <span className="checkbox-subtitle">Check this if you need a GST invoice for business purposes</span>
                </label>
              </div> */}

              {isGSTRequired && (
                <div className="gst-billing-form">
                  <h3>GST Billing Information</h3>
                  <div className="gst-form-content">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Business/Person Name *</label>
                        <input
                          type="text"
                          name="billingName"
                          value={gstBillingInfo.billingName}
                          onChange={handleGSTInputChange}
                          placeholder="Enter business or person name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>GST Number</label>
                        <input
                          type="text"
                          name="gstNumber"
                          value={gstBillingInfo.gstNumber}
                          onChange={handleGSTInputChange}
                          placeholder="22AAAAA0000A1Z5 (Optional)"
                          maxLength={15}
                          style={{ textTransform: 'uppercase' }}
                        />
                        <small className="form-hint">Enter 15-character GST number (optional for individual customers)</small>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Address Line 1 *</label>
                        <input
                          type="text"
                          name="billingAddressLine1"
                          value={gstBillingInfo.billingAddressLine1}
                          onChange={handleGSTInputChange}
                          placeholder="Street address, building, etc."
                          maxLength={100}
                          required
                        />
                        <small style={{ 
                          display: 'block', 
                          marginTop: '4px', 
                          fontSize: '12px', 
                          color: '#6c757d', 
                          fontStyle: 'italic' 
                        }}>
                          Maximum 100 characters
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Address Line 2</label>
                        <input
                          type="text"
                          name="billingAddressLine2"
                          value={gstBillingInfo.billingAddressLine2}
                          onChange={handleGSTInputChange}
                          placeholder="Apartment, suite, etc. (optional)"
                          maxLength={100}
                        />
                        <small style={{ 
                          display: 'block', 
                          marginTop: '4px', 
                          fontSize: '12px', 
                          color: '#6c757d', 
                          fontStyle: 'italic' 
                        }}>
                          Maximum 100 characters
                        </small>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          name="city"
                          value={gstBillingInfo.city}
                          onChange={handleGSTInputChange}
                          placeholder="Enter city"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>State *</label>
                        <input
                          type="text"
                          name="state"
                          value={gstBillingInfo.state}
                          onChange={handleGSTInputChange}
                          placeholder="Enter state"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Pincode *</label>
                        <input
                          type="text"
                          name="pincode"
                          value={gstBillingInfo.pincode}
                          onChange={handleGSTInputChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength={6}
                          pattern="[0-9]{6}"
                          inputMode="numeric"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={gstBillingInfo.phone}
                          onChange={handleGSTInputChange}
                          placeholder="Enter phone number"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={gstBillingInfo.email}
                          onChange={handleGSTInputChange}
                          placeholder="Enter email address"
                          required
                        />
                      </div>
                      <div className="form-group checkbox-group">
                        <label className="default-checkbox">
                          <input
                            type="checkbox"
                            name="isDefault"
                            checked={gstBillingInfo.isDefault}
                            onChange={handleGSTInputChange}
                          />
                          <span className="checkbox-text">Save as default GST billing information</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Right Side - Order Summary */}
          <div className="order-summary-section">
            <div className="order-summary">
              <h2>{groupBookingData ? 'Booking Summary' : (location.state?.serviceName ? 'Booking Summary' : 'Order Summary')}</h2>
              
              {/* Service Items */}
              <div className="order-items">
                {groupBookingData ? (
                  // Show group booking details
                  <div className="group-booking-summary">
                    {isFromOrdersPage && groupBookingData.groupBookingId && location.state?.groupServices?.length > 1 && (
                      <div className="group-payment-notice">
                        <h4>💳 Group Booking Payment</h4>
                        <div>
                          <p>Payment for all services in group booking <strong>{groupBookingData.groupBookingId}</strong>:</p>
                          <ul className="group-services-list">
                            {location.state.groupServices.map((service: any, index: number) => (
                              <li key={index}>
                                <span className="service-name">{service.serviceName}</span>
                                <span className="service-amount">₹{typeof service.amount === 'string' ? parseFloat(service.amount).toLocaleString() : service.amount?.toLocaleString()}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    {(groupBookingData.serviceRequests?.length || 1) > 1 && (
                      <div className="group-booking-header">
                        <h4>Group Booking - {groupBookingData.serviceRequests?.length || 1} Services</h4>
                        <p className="group-booking-subtitle">Multiple services in one booking</p>
                      </div>
                    )}
                    
                    {(groupBookingData.serviceRequests || []).map((service: any, index: number) => (
                      <div key={index} className="group-service-item">
                        <div className="service-index">{index + 1}</div>
                        <div className="service-content">
                          <div className="service-header">
                            <h5>{service.serviceName}</h5>
                            {service.planName && <span className="plan-name">{service.planName}</span>}
                          </div>
                          <div className="service-details">
                            <div className="service-schedule">
                              <span className="schedule-item">📅 {service.selectedDate}</span>
                              <span className="schedule-item">⏰ {service.selectedTime}</span>
                            </div>
                            <div className="service-address">
                              {service.selectedAddress 
                                ? `📍 ${service.selectedAddress?.name || service.selectedAddress?.label}`
                                : '🕉️ Antaryatra Temple Site, Varanasi'
                              }
                            </div>
                            <div className="service-price">
                              {(() => {
                                // Get the base price from selectedPlan if available
                                const selectedPlan = service.selectedPlan;
                                let basePrice = 0;
                                
                                if (selectedPlan?.dbPlan?.basePrice) {
                                  // Use backend pricing if available
                                  basePrice = selectedPlan.dbPlan.basePrice;
                                } else if (selectedPlan?.price) {
                                  // Parse price from plan price string
                                  basePrice = parseFloat(selectedPlan.price.replace(/[^0-9.-]+/g, ""));
                                } else if (service.priceEstimate) {
                                  // Fallback to priceEstimate (should be base price)
                                  basePrice = typeof service.priceEstimate === 'number' 
                                    ? service.priceEstimate 
                                    : parseFloat(service.priceEstimate.replace(/[^0-9.-]+/g, ""));
                                }
                                
                                return `₹${basePrice.toLocaleString()}`;
                              })()} 
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : location.state?.serviceName ? (
                  // Show service booking details
                  <div className="service-booking-item">
                    {/* Header Section */}
                    <div className="booking-service-header">
                      <div className="booking-service-image">
                        <img src={location.state.serviceImage || '/images/carousel/default-service.jpg'} alt={location.state.serviceName} />
                      </div>
                      <div className="booking-service-title">
                        <h4>{location.state.serviceName}</h4>
                        {location.state.planName && <p className="booking-service-subtitle">{location.state.planName}</p>}
                      </div>
                    </div>

                    {/* Content Grid */}
                    <div className="booking-details-grid">
                      {/* Schedule Details */}
                      <div className="booking-info-section">
                        <h5><span className="icon">📅</span> Schedule Details</h5>
                        <ul className="booking-info-list">
                          <li>
                            <span className="label">Date</span>
                            <span className="value">{location.state.selectedDate}</span>
                          </li>
                          <li>
                            <span className="label">Time</span>
                            <span className="value">{location.state.selectedTime}</span>
                          </li>
                          {location.state.duration && (
                            <li>
                              <span className="label">Duration</span>
                              <span className="value">{location.state.duration}</span>
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Service Details */}
                      <div className="booking-info-section">
                        <h5><span className="icon">🕉️</span> Service Details</h5>
                        <ul className="booking-info-list">
                          <li>
                            <span className="label">Service</span>
                            <span className="value">{location.state.serviceName}</span>
                          </li>
                          {location.state.planName && (
                            <li>
                              <span className="label">Plan</span>
                              <span className="value">{location.state.planName}</span>
                            </li>
                          )}
                          <li>
                            <span className="label">Amount</span>
                            <span className="value">
                              ₹{(() => {
                                // Use baseAmount if available (from cart), otherwise calculate from totalAmount
                                if (location.state.baseAmount) {
                                  const baseAmount = typeof location.state.baseAmount === 'string' 
                                    ? parseFloat(location.state.baseAmount.replace(/[^0-9.-]+/g, ""))
                                    : location.state.baseAmount;
                                  return baseAmount.toFixed(2);
                                } else {
                                  // Fallback: calculate from GST-inclusive totalAmount
                                  const totalAmount = typeof location.state.totalAmount === 'string' 
                                    ? parseFloat(location.state.totalAmount.replace(/[^0-9.-]+/g, ""))
                                    : location.state.totalAmount;
                                  const baseAmount = totalAmount / 1.18; // Remove 18% GST
                                  return baseAmount.toFixed(2);
                                }
                              })()} <span className="price-note">(incl. GST)</span>
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Service Address */}
                    <div className="booking-address">
                      <h5><span className="icon">📍</span> Service Address</h5>
                      {location.state.selectedAddress ? (
                        <>
                          <div className="address-type">{location.state.selectedAddress?.type || 'Address'}</div>
                          <div className="address-details">
                            <strong>{location.state.selectedAddress?.name || location.state.selectedAddress?.label}</strong><br />
                            {location.state.selectedAddress?.addressLine1 || location.state.selectedAddress?.line1}<br />
                            {(location.state.selectedAddress?.addressLine2 || location.state.selectedAddress?.line2) && (
                              <>{location.state.selectedAddress.addressLine2 || location.state.selectedAddress.line2}<br /></>
                            )}
                            {location.state.selectedAddress?.cityName || location.state.selectedAddress?.city}, {location.state.selectedAddress?.stateName || location.state.selectedAddress?.state} - {location.state.selectedAddress?.pincode || location.state.selectedAddress?.postalCode}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="address-type temple">🕉️ Sacred Site</div>
                          <div className="address-details antaryatra-address">
                            <strong>Antaryatra Temple Site</strong><br />
                            Sacred Temple Complex<br />
                            Varanasi, Uttar Pradesh - 221001<br />
                            <span className="temple-note">✨ Ritual will be performed at our dedicated temple site</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  // Show cart items
                  cartItems.map((item) => (
                    <div key={`${item.id}-${item.planId || 'default'}`} className="order-item">
                      <div className="item-details">
                        <h4>{item.name}</h4>
                        <p className="item-category">{item.category}</p>
                        {item.duration && <p className="item-duration">Duration: {item.duration}</p>}
                        {item.planName && <p className="item-plan">Plan: {item.planName}</p>}
                        <p className="item-quantity">Quantity: {item.quantity}</p>
                      </div>
                      <div className="item-price">
                        ₹{(() => {
                          const basePrice = parseFloat(item.price.replace(/[^0-9.-]+/g, ""));
                          const gstInclusivePrice = basePrice * 1.18; // Add 18% GST
                          return (gstInclusivePrice * item.quantity).toFixed(2);
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Discount Code - Hide for orders page payments including pay-later */}
              {!isFromOrdersPage && 
               !location.state?.isRetryPayment && 
               !location.state?.isPostpaidPayment && 
               !location.state?.orderId && (
                <div className="discount-section">
                  <h3>Discount Code</h3>
                  <div className="discount-input">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      placeholder="Enter discount code"
                      disabled={isLoadingDiscounts}
                    />
                    <button 
                      onClick={handleDiscountApply} 
                      className="apply-btn"
                      disabled={isLoadingDiscounts}
                    >
                      {isLoadingDiscounts ? 'Validating...' : 'Apply'}
                    </button>
                  </div>
                  
                  {/* Show applied discount */}
                  {appliedDiscount && (
                    <p className="discount-applied">
                      ✅ {appliedDiscount.code}: {appliedDiscount.percentage}% discount applied! 
                      <span className="discount-description">({appliedDiscount.description})</span>
                    </p>
                  )}
                  
                  {/* Show error message */}
                  {discountError && (
                    <p className="discount-error">
                      ❌ {discountError}
                    </p>
                  )}
                  
                  {/* Show available discount codes */}
                  {!isLoadingDiscounts && (availableDiscounts.length > 0) && (
                    <div className="available-codes">
                      <p><strong>Available discount codes:</strong></p>
                      <div className="discount-list">
                        {availableDiscounts.map((discount, index) => (
                          <div 
                            key={index} 
                            className="discount-item clickable"
                            onClick={() => setDiscountCode(discount.code)}
                            title="Click to use this code"
                          >
                            <div className="discount-code-row">
                              <code>{discount.code}</code>
                            </div>
                            <div className="discount-details-row">
                              <span className="discount-percentage">{discount.percentage}% off</span>
                              <small className="discount-requirement">({discount.requirement})</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Debug info - remove in production */}
                  {/* {process.env.NODE_ENV === 'development' && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                      Debug: {availableDiscounts.length} discounts loaded, isLoading: {isLoadingDiscounts.toString()}
                    </div>
                  )} */}
                  
                  {/* Loading indicator for discounts */}
                  {isLoadingDiscounts && availableDiscounts.length === 0 && (
                    <p className="loading-discounts">Loading available discounts...</p>
                  )}
                </div>
              )}

              {/* Price Breakdown */}
              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subtotal ({getItemCount()} {groupBookingData ? (groupBookingData.serviceRequests?.length === 1 ? 'service' : 'services') : (location.state?.serviceName ? 'service' : 'items')})</span>
                  <span>₹{getSubtotal().toFixed(2)}</span>
                </div>
                {appliedDiscount && (
                  <div className="price-row discount">
                    <span>Discount ({appliedDiscount.code} - {appliedDiscount.percentage}%)</span>
                    <span>-₹{getDiscountAmount().toFixed(2)}</span>
                  </div>
                )}
                <div className="price-row gst-row">
                  <span>GST (18%)</span>
                  {getTaxAmount() > 0
                    ? <span>₹{getTaxAmount().toFixed(2)}</span>
                    : <span className="gst-included-label">Included</span>
                  }
                </div>
                <div className="price-row total">
                  <span>Total Amount</span>
                  <span>₹{getTotalAmount().toFixed(2)}</span>
                </div>
              </div>

              {/* Inline validation error */}
              {inlineError && (
                <div ref={inlineErrorRef} className="payment-inline-error" role="alert">
                  <span>⚠️ {inlineError}</span>
                  <button className="payment-inline-error-close" onClick={() => setInlineError('')} aria-label="Dismiss">×</button>
                </div>
              )}

              {/* Payment Button */}
              <button 
                onClick={handlePayment} 
                className={`pay-now-btn ${isProcessing ? 'processing' : ''} ${paymentMethod === 'paylater' ? 'pay-later-btn' : ''}`}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner"></span>
                    {paymentMethod === 'paylater' ? 'Confirming Booking...' : 'Processing Payment...'}
                  </>
                ) : paymentMethod === 'paylater' ? (
                  <>
                    <span className="book-icon">📅</span>
                    {location.state?.serviceName ? 
                      `Confirm Service Booking - ₹${getTotalAmount().toFixed(2)}` :
                      `Confirm Booking - ₹${getTotalAmount().toFixed(2)}`
                    }
                    <small>Pay later before {location.state?.serviceName ? 'service' : 'ceremony'}</small>
                  </>
                ) : (
                  `Pay ₹${getTotalAmount().toFixed(2)}`
                )}
              </button>

              {/* Payment Error & Retry */}
              {paymentError && paymentMethod !== 'paylater' && (
                <div className="payment-error-section">
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <span>{paymentError}</span>
                  </div>
                  {lastBookingId && (
                    <div className="retry-section">
                      <p>Your booking has been created but payment failed. You can retry payment:</p>
                      <button 
                        onClick={handleRetryPayment} 
                        className="retry-payment-btn"
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Processing...' : `Retry Payment - ₹${getTotalAmount().toFixed(2)}`}
                      </button>
                      <small>Booking ID: {lastBookingId.slice(0, 8)}...</small>
                    </div>
                  )}
                  {!lastBookingId && (
                    <div className="retry-section">
                      <p>Please try booking again or contact support if the issue persists.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Pay Later Info */}
              {paymentMethod === 'paylater' && (
                <div className="pay-later-info">
                  <div className="info-header">
                    <span className="info-icon">ℹ️</span>
                    <span>Pay Later Details</span>
                  </div>
                  <ul className="pay-later-benefits">
                    <li>✅ Reserve your booking instantly</li>
                    <li>⏰ Pay anytime before the ceremony</li>
                    <li>🔄 Free cancellation until payment</li>
                    <li>📧 Payment reminders via email/SMS</li>
                  </ul>
                </div>
              )}

              {/* Security Info */}
              <div className="security-info">
                <div className="security-item">
                  <span className="security-icon">🔒</span>
                  <span>SSL Encrypted {paymentMethod === 'paylater' ? 'Booking' : 'Payment'}</span>
                </div>
                <div className="security-item">
                  <span className="security-icon">🛡️</span>
                  <span>100% Secure Transaction (Razorpay)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky mobile pay bar — always visible, no scrolling needed */}
      <div className="payment-sticky-mobile-bar">
        {!paymentMethod && (
          <p className="payment-sticky-hint">👆 Select a payment method above</p>
        )}
        <button
          onClick={handlePayment}
          className={`payment-sticky-btn ${isProcessing ? 'processing' : ''} ${!paymentMethod ? 'unselected' : ''}`}
          disabled={isProcessing}
        >
          {isProcessing
            ? 'Processing...'
            : paymentMethod === 'paylater'
            ? `Confirm Booking · ₹${getTotalAmount().toFixed(2)}`
            : `Pay ₹${getTotalAmount().toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;